import { fg } from '@atlaskit/platform-feature-flags/fg';

import { SAINFeatureControl } from '../../../../common/utils/sain-feature-control';

/**
 * Build a deterministic keyword JQL query of the form `textfields ~ "<query>*"`.
 *
 * This mirrors the NIN translation strategy used when a query goes from Quick
 * Find to the new Issue Navigator and is the FE counterpart to the backend's
 * `JIRA_NLQ_KEYWORD` suppression path introduced in xp-knowledge-discovery
 * PR #1611.
 *
 * The user query is sanitised before being interpolated so that the resulting
 * JQL string is always well-formed. In every case:
 *
 *  - Surrounding whitespace is trimmed.
 *  - Backslashes are escaped first (`\` → `\\`).
 *  - Double quotes are escaped so the surrounding quoted JQL string stays
 *    valid (`"` → `\"`).
 *
 * Wildcard handling is gated behind `kd_sain_jira_card_keyword_jql_escaping`
 * (JSAI-621).
 *
 * With the gate ON, the Lucene/JQL wildcard characters `*` and `?` are NOT
 * escaped. Escaping them made Lucene match a literal asterisk or question mark,
 * which is not indexed, so a query such as `fix * login` returned zero results.
 * When a user types a wildcard they mean it as a wildcard, so it is passed
 * through to the JQL untouched.
 *
 * With the gate ON, the trailing prefix-match `*` is also only appended when the
 * trimmed query does not already end in `"`, `*`, or `?`:
 *
 *  - Ending in `"` closes a quoted phrase. Lucene parses an appended `*` as a
 *    new term beginning with a wildcard, which is a syntax error.
 *  - Ending in `*` or `?` means the user already supplied a trailing wildcard,
 *    so appending ours would produce `**` or `?*`.
 *
 * Known limitation of the gate-ON behaviour: a wildcard that starts a term, such
 * as `*login` or the stray `*` in `fix * login`, is rejected by Lucene with "the
 * '*' and '?' are not allowed as first character in wildcard query". The query is
 * passed through verbatim to respect the user's query rather than rewritten,
 * so those inputs still surface that error.
 *
 * With the gate OFF, the legacy behaviour is preserved: `*` and `?` are escaped
 * so they are matched literally, and the trailing `*` is always appended.
 *
 * If the query is empty or whitespace-only the function returns `null` so the
 * caller can decide how to handle the no-op case (e.g. surface an error card).
 *
 * Ordering: when the Jira keyword card "order by created" treatment is enabled
 * (SAIN UX-layer experiments `kd_jira_card_order_by_hello` /
 * `kd_jira_card_order_by_customer`, or the `kd_sain_jira_card_order_by_created`
 * shipped gate), the well-formed JQL string is suffixed with
 * `ORDER BY created DESC`. This applies to both escaping branches so it is
 * independent of `kd_sain_jira_card_keyword_jql_escaping`.
 *
 * Trailing prefix-match wildcard on a non-alphanumeric final term: when the
 * `JiraCardNonAlphaNumWildcard` treatment (or the
 * `kd_sain_jira_card_non_alphanum_wildcard` shipped gate) is enabled, the trailing
 * `*` is omitted whenever the final whitespace-delimited term contains a
 * non-alphanumeric character. Letters and digits of any script
 * (accented Latin, CJK, Cyrillic, non-ASCII digits) count as alphanumeric.
 */
export const buildKeywordJql = (rawQuery: string): string | null => {
	const trimmed = rawQuery.trim();
	if (trimmed.length === 0) {
		return null;
	}

	// Read the assigned treatment values; enrollment/exposure is fired at the
	// high-level search entry point, so these are memoized reads only.
	const orderByClause = SAINFeatureControl.isJiraCardOrderByCreatedEnabled()
		? ' ORDER BY created DESC'
		: '';

	// A trailing `*` on a final term that contains a non-indexable character matches
	// nothing, so drop it for that term when the treatment is enabled. Only the
	// final term matters because the trailing `*` attaches to it.
	const lastTerm = trimmed.split(/\s+/).pop() ?? '';
	const suppressTrailingWildcard =
		SAINFeatureControl.isJiraCardNonAlphaNumWildcardEnabled() &&
		/[^\p{L}\p{N}\p{M}]/u.test(lastTerm);

	// Escape backslashes first so the subsequent escapes don't get re-escaped,
	// then escape double quotes because they delimit the JQL string.
	const escapedBase = trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

	if (!fg('kd_sain_jira_card_keyword_jql_escaping')) {
		// Legacy behaviour: also escape the JQL wildcards `*` and `?` so user
		// input is matched literally, and append the trailing `*` unless it is
		// suppressed for a non-alphanumeric final term.
		const escapedLegacy = escapedBase.replace(/\*/g, '\\*').replace(/\?/g, '\\?');
		const suffix = suppressTrailingWildcard ? '' : '*';

		return `textfields ~ "${escapedLegacy}${suffix}"${orderByClause}`;
	}

	// Wildcards are intentionally left alone so the user's `*` and `?` behave as
	// they typed them.
	//
	// The suffix check runs against the trimmed input rather than the escaped
	// output so a real trailing quote is detected instead of the escaped `\"`.
	const endsWithWildcardOrQuote = /["*?]$/.test(trimmed);
	const suffix = endsWithWildcardOrQuote || suppressTrailingWildcard ? '' : '*';

	return `textfields ~ "${escapedBase}${suffix}"${orderByClause}`;
};
